import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Chip,
    Typography,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    TextField,
    Button,
    Stack,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import InstagramIcon from '@mui/icons-material/Instagram';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';

export const conversationMessageIcon = (type) => {
    if (type === "TYPE_INSTAGRAM") {
        return <InstagramIcon sx={{ fontSize: "18px", color: "white", opacity: 0.8, margin: "6px" }} />;
    }
    if (type === "TYPE_SMS") {
        return <ChatBubbleIcon sx={{ fontSize: "18px", color: "white", opacity: 0.8, margin: "6px" }} />;
    }
    return null;
};

const Conversations = ({ conversations, syncConversations, currentUser }) => {
    const [open, setOpen] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [localMessages, setLocalMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newMessage, setNewMessage] = useState("");
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);
    const [selectedMessageType, setSelectedMessageType] = useState("IG");
    const [conversationFilter, setConversationFilter] = useState("IG");

    const handleConversationClick = async (conversation) => {
        setSelectedConversation(conversation);
        setOpen(true);
        setLoading(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/ghl/conversations/${conversation.id}/messages`
            );
            const responseLocal = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/ghl/local-conversations/${conversation.id}/messages`
            );

            const data = await response.json();
            const localData = await responseLocal.json();

            const remoteMessages = data.data || [];
            const localMessagesData = localData.data || [];

            const mergedMessages = remoteMessages.map((msg) => {
                if (msg.direction === "outbound") {
                    const msgTime = new Date(msg.dateAdded).getTime();
                    const matchingLocal = localMessagesData.find((lm) => {
                        const localTime = new Date(lm.dateAdded).getTime();
                        return (
                            lm.body === msg.body &&
                            lm.direction === "outbound" &&
                            Math.abs(msgTime - localTime) < 2000
                        );
                    });
                    if (matchingLocal && matchingLocal.sentByUser) {
                        return {
                            ...msg,
                            sentByUser: matchingLocal.sentByUser,
                            sentByUserId: matchingLocal.sentByUserId,
                        };
                    }
                }
                return msg;
            });

            const sanitizedMessages = mergedMessages.map((msg) => ({
                ...msg,
                body: msg.body || "[Attachment]",
            }));

            setMessages(sanitizedMessages);
            setLocalMessages(localMessagesData);
        } catch (error) {
            console.error("Error fetching conversation messages:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !currentUser || !selectedConversation) {
            return;
        }

        setSending(true);

        const isSMS = selectedMessageType === "SMS";
        const endpoint = isSMS ? "/ghl/messages/send-sms" : "/ghl/messages/send";
        const payload = {
            contactId: selectedConversation.contactId,
            message: newMessage.trim(),
            type: isSMS ? "SMS" : "IG",
            sentByUser: currentUser.username,
            sentByUserId: currentUser.id,
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorDetails = await response.text();
                throw new Error(`Failed to send message: ${response.status} - ${errorDetails}`);
            }

            const sentMessage = {
                body: newMessage.trim(),
                messageType: isSMS ? "TYPE_SMS" : "TYPE_INSTAGRAM",
                direction: "outbound",
                dateAdded: new Date().toISOString(),
                sentByUser: currentUser.username,
                sentByUserId: currentUser.id,
            };

            setMessages((prevMessages) => [...prevMessages, sentMessage]);
            setNewMessage("");
        } catch (error) {
            console.error("Error sending message:", error);
        } finally {
            setSending(false);
        }
    };

    const handleSendMessageWithAttachment = async () => {
        if (!newMessage.trim() || !currentUser || !selectedConversation) {
            return;
        }

        setSending(true);

        const isSMS = selectedMessageType === "SMS";
        const endpoint = isSMS ? "/ghl/messages/send-sms" : "/ghl/messages/send";
        const payload = {
            contactId: selectedConversation.contactId,
            message: newMessage.trim(),
            type: isSMS ? "SMS" : "IG",
            sentByUser: currentUser.username,
            sentByUserId: currentUser.id,
            attachments: ["https://static-assets.internal.usercontent.site/conversations-assets/location/ZghV6U2wnAogcW6SRaAr/conversations/601xcnRBLUUbOTXziMYO/4600426d-57e3-40da-87cf-6f05c2fc8468.mp3"],
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorDetails = await response.text();
                throw new Error(`Failed to send message with attachment: ${response.status} - ${errorDetails}`);
            }

            const sentMessage = {
                body: newMessage.trim(),
                messageType: isSMS ? "TYPE_SMS" : "TYPE_INSTAGRAM",
                direction: "outbound",
                dateAdded: new Date().toISOString(),
                sentByUser: currentUser.username,
                sentByUserId: currentUser.id,
                attachments: ["https://static-assets.internal.usercontent.site/conversations-assets/location/ZghV6U2wnAogcW6SRaAr/conversations/601xcnRBLUUbOTXziMYO/4600426d-57e3-40da-87cf-6f05c2fc8468.mp3"],
            };

            setMessages((prevMessages) => [...prevMessages, sentMessage]);
            setNewMessage("");
        } catch (error) {
            console.error("Error sending message with attachment:", error);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        if (!loading && open && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
    }, [messages, loading, open]);

    useEffect(() => {
        syncConversations();

        const intervalId = setInterval(() => {
            syncConversations();
        }, 120000);

        return () => clearInterval(intervalId);
    }, []);

    const filteredConversations = conversations.filter((conversation) => {
        if (conversationFilter === "IG") {
            return conversation.lastMessageType === "TYPE_INSTAGRAM";
        } else if (conversationFilter === "SMS") {
            return conversation.lastMessageType === "TYPE_SMS";
        }
        return true;
    });

    if (!conversations || conversations.length === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    backgroundColor: "#1E1E1E",
                }}
            >
                <CircularProgress sx={{ color: "#98FF98" }} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                padding: "16px",
                backgroundColor: "#1E1E1E",
                borderRadius: "12px",
                color: "#FFF",
            }}
        >
            <Typography variant="h6" sx={{ color: "#98FF98", marginBottom: "16px" }}>
                Inbox
            </Typography>
            <Box sx={{ mb: 2, display: "flex" }}>
                <ToggleButtonGroup
                    value={conversationFilter}
                    exclusive
                    onChange={(event, newFilter) => {
                        if (newFilter) setConversationFilter(newFilter);
                    }}
                    aria-label="Conversation Filter"
                >
                    <ToggleButton value="IG" aria-label="Instagram Conversations" sx={{ width: "120px" }}>
                        <InstagramIcon sx={{ fontSize: "18px", marginRight: "2px" }} /> IG
                    </ToggleButton>
                    <ToggleButton value="SMS" aria-label="SMS Conversations" sx={{ width: "120px" }}>
                        <ChatBubbleIcon sx={{ fontSize: "18px", marginRight: "2px" }} /> SMS
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <List>
                {filteredConversations.map((conversation, index) => (
                    <ListItem
                        key={index}
                        sx={{
                            borderBottom: "1px solid #2A2A2A",
                            padding: "8px 0",
                            "&:hover": { backgroundColor: "#2E2E2E" },
                            cursor: "pointer",
                        }}
                        onClick={() => handleConversationClick(conversation)}
                    >
                        <ListItemAvatar>
                            <Avatar
                                alt={conversation.contactName}
                                src={conversation.avatar || ""}
                                sx={{ backgroundColor: "#444", color: "#98FF98" }}
                            >
                                {conversation.contactName.charAt(0).toUpperCase()}
                            </Avatar>
                        </ListItemAvatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ListItemText
                                primary={conversation.contactName}
                                primaryTypographyProps={{ sx: { fontWeight: "bold" } }}
                                secondary={
                                    <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
                                        {conversation.lastMessageDirection === "outbound" ? (
                                            <Chip
                                                label="Replied"
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#98FF98",
                                                    color: "#000",
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label="Unanswered"
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#444",
                                                    color: "#999",
                                                }}
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                color: "#999",
                                            }}
                                        >
                                            {conversation.lastMessageBody}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{ color: "#666", marginLeft: "16px", whiteSpace: "nowrap" }}
                        >
                            {new Date(conversation.lastMessageDate).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </Typography>
                    </ListItem>
                ))}
            </List>
            <Dialog open={open} onClose={() => {
                setOpen(false);
                syncConversations();
            }} fullWidth maxWidth="sm">
                <DialogTitle
                    sx={{ backgroundColor: "#121212", display: "flex", alignItems: "center", gap: "8px" }}
                >
                    <Avatar
                        alt={selectedConversation?.contactName}
                        src={selectedConversation?.avatar || ""}
                        sx={{ backgroundColor: "#444", color: "#98FF98" }}
                    >
                        {selectedConversation?.contactName?.charAt(0).toUpperCase()}
                    </Avatar>
                    {selectedConversation?.contactName || "Unknown"}
                </DialogTitle>
                <DialogContent
                    sx={{
                        backgroundColor: "#121212",
                        maxHeight: "70vh",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {loading ? (
                        <Stack alignItems="center" sx={{ padding: "16px" }}>
                            <CircularProgress sx={{ color: "#98FF98" }} />
                        </Stack>
                    ) : (
                        <>
                            <Box>
                                {messages
                                    .slice()
                                    .sort((a, b) => new Date(a.dateAdded) - new Date(b.dateAdded))
                                    .map((message, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                margin: "8px 0",
                                                display: "flex",
                                                justifyContent:
                                                    message.direction === "outbound" ? "flex-end" : "flex-start",
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" mb={0.5}>
                                                {conversationMessageIcon(message.messageType)}
                                            </Box>
                                            <Box
                                                sx={{
                                                    textAlign: "left",
                                                    backgroundColor:
                                                        message.direction === "outbound"
                                                            ? "#2A2A2A"
                                                            : "#1E1E1E",
                                                    padding: "8px",
                                                    borderRadius: "8px",
                                                    maxWidth: "80%",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color:
                                                            message.direction === "outbound"
                                                                ? "#98FF98"
                                                                : "#FFF",
                                                    }}
                                                >
                                                    {message.body || "[Attachment]"}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: "#666",
                                                        display: "block",
                                                        marginTop: "4px",
                                                    }}
                                                >
                                                    {message.direction === "outbound" ? (
                                                        <>
                                                            <span style={{ color: "#ccc", fontWeight: "bold" }}>
                                                                {message.source === "workflow" ? (
                                                                    <>
                                                                        <SmartToyIcon sx={{ fontSize: 16, marginBottom: "-3px" }} /> BOT
                                                                    </>
                                                                ) : (
                                                                    message.sentByUser === "System" ? "User" :
                                                                        <>
                                                                            <PersonIcon sx={{ fontSize: 16, marginBottom: "-3px" }} /> {message.sentByUser}
                                                                        </>
                                                                )}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        ""
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: "#666",
                                                        display: "block",
                                                        marginTop: "4px",
                                                    }}
                                                >
                                                    {new Date(message.dateAdded).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                <div ref={messagesEndRef} />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        backgroundColor: "#121212",
                        padding: "8px 16px",
                        flexDirection: "column",
                    }}
                >
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 1 }}>
                        <ToggleButtonGroup
                            value={selectedMessageType}
                            exclusive
                            onChange={(event, newType) => {
                                if (newType) setSelectedMessageType(newType);
                            }}
                            aria-label="Message Type"
                            sx={{ width: "100%" }}
                        >
                            <ToggleButton value="IG" aria-label="Instagram" sx={{ width: "80px" }}>
                                <InstagramIcon sx={{ fontSize: "18px", marginRight: "2px" }} /> IG
                            </ToggleButton>
                            <ToggleButton value="SMS" aria-label="SMS" sx={{ width: "80px" }}>
                                <ChatBubbleIcon sx={{ fontSize: "18px", marginRight: "2px" }} /> SMS
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
                        <TextField
                            fullWidth
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            multiline
                            minRows={2}
                            maxRows={4}
                            disabled={sending}
                            sx={{
                                input: {
                                    backgroundColor: "#1E1E1E",
                                    color: "#DDD",
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#555",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#999",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#999",
                                    },
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            sx={{
                                height: "78px",
                                width: "104px",
                                backgroundColor: "#999",
                                color: "#000",
                                marginLeft: "8px",
                                "&:hover": {
                                    backgroundColor: "#7BD97B",
                                },
                            }}
                            onClick={handleSendMessage}
                            disabled={sending || newMessage.trim() === ""}
                        >
                            {sending ? (
                                <CircularProgress sx={{ fontSize: 40 }} />
                            ) : (
                                <SendIcon sx={{ fontSize: 45 }} />
                            )}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                height: "78px",
                                width: "140px",
                                backgroundColor: "#999",
                                color: "#000",
                                marginLeft: "8px",
                                "&:hover": {
                                    backgroundColor: "#7BD97B",
                                },
                            }}
                            onClick={handleSendMessageWithAttachment}
                            disabled={sending || newMessage.trim() === ""}
                        >
                            {sending ? (
                                <CircularProgress sx={{ fontSize: 40 }} />
                            ) : (
                                "Send w/ Att"
                            )}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Conversations;
